import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const NotFoundPageLayout = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - var(--site-header-offset));
  min-height: calc(100dvh - var(--site-header-offset));
  padding: ${({ theme }) => theme.spacing['3xl']} ${({ theme }) => theme.spacing.md};
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing['4xl']} ${({ theme }) => theme.spacing.lg};
  }
`;

const NotFoundContent = styled.div`
  width: min(100%, 36rem);
`;

const NotFoundHeading = styled.h1`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.semantic.textPrimary};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.display};
  font-size: clamp(2rem, 8vw, 3.5rem);
  line-height: 1.1;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
    outline-offset: 6px;
  }
`;

const NotFoundMessage = styled.p`
  margin: 0 auto ${({ theme }) => theme.spacing.xl};
  max-width: 30rem;
  color: ${({ theme }) => theme.colors.semantic.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  line-height: 1.6;
`;

const ActionList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ActionLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.lg}`};
  border: 1px solid ${({ theme }) => theme.colors.semantic.borderStrong};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.semantic.surface};
  color: ${({ theme }) => theme.colors.semantic.textPrimary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  text-decoration: none;

  &:hover {
    border-color: ${({ theme }) => theme.colors.semantic.focus};
    background: ${({ theme }) => theme.colors.semantic.surfaceRaised};
    color: ${({ theme }) => theme.colors.semantic.textPrimary};
  }
`;

export function NotFoundPage() {
  const location = useLocation();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const previousTitle = document.title;
    const pageTitle = 'Page not found | Joanne Art Class';

    document.title = pageTitle;
    // This route owns its centered layout. Reset only its entry scroll before
    // focusing the heading so a scrolled source page cannot hide it under the
    // fixed public header. The deferred reset also wins over the browser's
    // history scroll restoration when Back re-enters this route.
    const resetScroll = () => window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    resetScroll();
    const resetScrollTimeout = window.setTimeout(resetScroll, 0);
    headingRef.current?.focus({ preventScroll: true });

    return () => {
      window.clearTimeout(resetScrollTimeout);
      if (document.title === pageTitle) {
        document.title = previousTitle;
      }
    };
  }, [location.pathname]);

  return (
    <NotFoundPageLayout aria-labelledby="not-found-heading">
      <NotFoundContent>
        <NotFoundHeading id="not-found-heading" ref={headingRef} tabIndex={-1}>
          Page not found
        </NotFoundHeading>
        <NotFoundMessage>
          We couldn&apos;t find that gallery page. Try returning home or browse the gallery to find something new.
        </NotFoundMessage>
        <ActionList>
          <ActionLink to="/">Home</ActionLink>
          <ActionLink to="/gallery">Browse gallery</ActionLink>
        </ActionList>
      </NotFoundContent>
    </NotFoundPageLayout>
  );
}

export default NotFoundPage;
